import { supabase } from '@/lib/supabase';

// ---------------------------------------------------------------------------
// Vitrine que vende: uma única chamada em vitrine_venda() devolve tudo pronto,
// já ordenado (à venda primeiro, depois por data). Nada de juntar tabelas.
// ---------------------------------------------------------------------------

export type AcaoVenda = 'comprar' | 'inscrever' | 'em_breve' | 'saiba_mais' | 'comprar_fichas';

export interface ItemVitrine {
  evento_id: number;
  slug: string;
  nome: string;
  categoria: string | null;
  inicio_em: string;
  fim_em: string | null;
  cidade: string | null;
  uf: string | null;
  local_nome: string | null;
  imagem_mobile_url: string | null;
  imagem_desktop_url: string | null;
  entrada_franca: boolean;
  so_divulgacao: boolean;
  vende_ingressos: boolean;
  a_venda: boolean;
  acao: AcaoVenda;
  preco_centavos: number | null;
  /** Menor preço das fichas da loja, em centavos (null quando não vende fichas). */
  fichas_a_partir_centavos: number | null;
  lote_nome: string | null;
  venda_ate: string | null;
  restantes: number | null;
  vendidos: number | null;
  proximo_nome: string | null;
  proximo_preco_centavos: number | null;
  proximo_abre_em: string | null;
  dias_para_evento: number | null;
  como_participar: string | null;
  whatsapp: string | null;
}

const BASE = 'https://www.seligacatolico.com.br';

export async function carregarVitrineVenda(): Promise<ItemVitrine[]> {
  const { data, error } = await supabase.rpc('vitrine_venda');
  if (error) throw error;
  const lista = (data as ItemVitrine[] | null) ?? [];
  return Array.isArray(lista) ? lista : [];
}

export function formatarMoeda(centavos: number): string {
  return (centavos / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function diaSP(d: Date): string {
  try {
    return new Intl.DateTimeFormat('sv-SE', { timeZone: 'America/Sao_Paulo' }).format(d);
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

/** "dd/mm" no horário de Brasília. */
export function dataCurta(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
  }).format(d);
}

/** Selo do cartaz: dia grande e mês em letras curtas. */
export function seloData(iso: string): { dia: string; mes: string } {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { dia: '', mes: '' };
  const dia = new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
  }).format(d);
  const mes = new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    month: 'short',
  })
    .format(d)
    .replace('.', '')
    .toUpperCase();
  return { dia, mes };
}

export function linkDoEvento(item: ItemVitrine): string {
  return `/e/${item.slug}`;
}

export function precoDoCartao(item: ItemVitrine): string | null {
  if (item.preco_centavos == null) return null;
  const valor = formatarMoeda(item.preco_centavos);
  if (item.lote_nome) return `${item.lote_nome} · ${valor}`;
  return `a partir de ${valor}`;
}

/** Preço curto da lista: "a partir de R$ 10,00". */
export function precoDaLista(item: ItemVitrine): string | null {
  if (item.preco_centavos == null) return null;
  return `a partir de ${formatarMoeda(item.preco_centavos)}`;
}

function diasAte(iso: string): number | null {
  const alvo = new Date(iso);
  if (Number.isNaN(alvo.getTime())) return null;
  const a = new Date(diaSP(alvo)).getTime();
  const b = new Date(diaSP(new Date())).getTime();
  return Math.round((a - b) / 86400000);
}

/**
 * Linha de urgência — SEMPRE presente quando existe dado verdadeiro, na ordem:
 * restantes (≤30) → venda até → dias para o evento.
 */
export function linhaDeUrgencia(item: ItemVitrine): string | null {
  if (item.restantes != null && item.restantes > 0 && item.restantes <= 30) {
    if (item.restantes === 1) return 'Último ingresso';
    return `Últimos ${item.restantes} ingressos`;
  }

  if (item.venda_ate) {
    const diff = diasAte(item.venda_ate);
    const lote = item.lote_nome;
    if (diff != null && diff <= 0) return lote ? `${lote} acaba hoje` : 'As vendas acabam hoje';
    if (diff === 1) return lote ? `${lote} acaba amanhã` : 'As vendas acabam amanhã';
    const quando = dataCurta(item.venda_ate);
    if (quando) return lote ? `${lote} até ${quando}` : `Vendas até ${quando}`;
  }

  if (item.dias_para_evento != null) {
    if (item.dias_para_evento <= 0) return 'É hoje!';
    if (item.dias_para_evento === 1) return 'É amanhã!';
    return `Faltam ${item.dias_para_evento} dias`;
  }

  return null;
}

/** Versão curta da mesma urgência, para a lista. */
export function linhaDeUrgenciaCurta(item: ItemVitrine): string | null {
  if (item.restantes != null && item.restantes > 0 && item.restantes <= 30) {
    if (item.restantes === 1) return 'Último ingresso';
    return `Últimos ${item.restantes}`;
  }

  if (item.venda_ate) {
    const diff = diasAte(item.venda_ate);
    if (diff != null && diff <= 0) return 'Acaba hoje';
    if (diff === 1) return 'Acaba amanhã';
    const quando = dataCurta(item.venda_ate);
    if (quando) return `Até ${quando}`;
  }

  if (item.dias_para_evento != null) {
    if (item.dias_para_evento <= 0) return 'É hoje!';
    if (item.dias_para_evento === 1) return 'É amanhã!';
    return `Faltam ${item.dias_para_evento} dias`;
  }

  return null;
}

export function vendidosTexto(item: ItemVitrine): string | null {
  if (item.vendidos == null || item.vendidos <= 0) return null;
  return `${item.vendidos} ${item.vendidos === 1 ? 'pessoa já garantiu' : 'pessoas já garantiram'}`;
}

export function proximoLoteTexto(item: ItemVitrine): string | null {
  if (!item.proximo_nome || !item.proximo_abre_em) return null;
  const preco =
    item.proximo_preco_centavos != null ? ` ${formatarMoeda(item.proximo_preco_centavos)}` : '';
  return `Depois: ${item.proximo_nome}${preco}`;
}

/** Evento de só divulgação que vende pelo WhatsApp do organizador. */
export function vendePeloWhatsApp(item: ItemVitrine): boolean {
  return item.so_divulgacao && !!item.whatsapp && item.whatsapp.replace(/\D/g, '').length >= 10;
}

/**
 * Quem entra em "À venda agora": ingressos à venda, fichas da loja à venda e
 * eventos de só divulgação com WhatsApp nos próximos 30 dias. Fichas nunca
 * caem em "Em breve" (é venda aberta, só com outro nome).
 */
export function entraNaFaixa(item: ItemVitrine): boolean {
  if (item.a_venda || vendeFichas(item)) return true;
  if (!vendePeloWhatsApp(item)) return false;
  const dias = item.dias_para_evento;
  return dias != null && dias >= 0 && dias <= 30;
}

/** Evento que está vendendo fichas da loja agora (entrada franca + produtos). */
export function vendeFichas(item: ItemVitrine): boolean {
  return item.acao === 'comprar_fichas';
}

/** Mesma tela de compra de fichas usada na página do evento. */
export function linkFichas(item: ItemVitrine): string {
  return `/e/${item.slug}/fichas`;
}

/** Link do botão principal: fichas têm tela própria; os outros vão para o evento. */
export function linkDaAcao(item: ItemVitrine): string {
  return vendeFichas(item) ? linkFichas(item) : linkDoEvento(item);
}

/** "Fichas a partir de R$ 60,00" (null quando a loja não informou preço). */
export function fichasAPartirTexto(item: ItemVitrine): string | null {
  if (item.fichas_a_partir_centavos == null) return null;
  return `Fichas a partir de ${formatarMoeda(item.fichas_a_partir_centavos)}`;
}

/** Botão principal do cartão grande. */
export function rotuloAcao(item: ItemVitrine): string {
  if (vendePeloWhatsApp(item)) return 'Chamar no WhatsApp';
  if (vendeFichas(item)) return 'Comprar fichas';
  if (item.acao === 'comprar') return 'Comprar ingresso';
  if (item.acao === 'inscrever') return 'Fazer inscrição';
  if (item.acao === 'em_breve') return 'Me avise';
  return 'Ver o evento';
}

/** Botão curto da lista. */
export function rotuloAcaoCurto(item: ItemVitrine): string {
  if (vendePeloWhatsApp(item)) return 'Chamar no WhatsApp';
  if (vendeFichas(item)) return 'Comprar fichas';
  if (item.acao === 'comprar') return 'Comprar';
  if (item.acao === 'inscrever') return 'Inscrever';
  if (item.acao === 'em_breve') return 'Me avise';
  return 'Ver';
}

/** Última linha da lista: preço ou estado. */
export function estadoDaLista(item: ItemVitrine): string {
  const preco = precoDaLista(item);
  if (item.a_venda && preco) return preco;
  if (vendePeloWhatsApp(item)) return 'Venda pelo WhatsApp';

  const fichas = fichasAPartirTexto(item);
  if (vendeFichas(item) && fichas) {
    return item.entrada_franca ? `Entrada franca · ${fichas}` : fichas;
  }

  if (item.entrada_franca) return 'Entrada franca';
  if (item.acao === 'em_breve') return 'Em breve';
  if (item.so_divulgacao) return 'Saiba como participar';
  if (item.vende_ingressos) return 'Ingressos em breve';
  return 'Inscrições em breve';
}

/** Texto do WhatsApp: nome · data em cidade + link do evento. */
export function textoCompartilhar(item: ItemVitrine): string {
  const onde = item.cidade ? ` em ${item.cidade}` : '';
  const dia = dataCurta(item.inicio_em);
  return `${item.nome} · ${dia}${onde}. Garanta o seu: ${BASE}/e/${item.slug}`;
}

export function linkWhatsApp(item: ItemVitrine): string {
  return `https://wa.me/?text=${encodeURIComponent(textoCompartilhar(item))}`;
}

/** Conversa direta com a organização (só divulgação / venda por WhatsApp). */
export function linkWhatsAppContato(item: ItemVitrine): string {
  const numero = (item.whatsapp ?? '').replace(/\D/g, '');
  const texto = `Olá! Quero participar do ${item.nome} (vi no Se Liga Católico).`;
  return `https://wa.me/${numero}?text=${encodeURIComponent(texto)}`;
}

export function diaMesCurto(iso: string): string {
  return dataCurta(iso);
}

// ---------------------------------------------------------------------------
// Cidades dos chips: usa só a primeira antes de "/" ou "," (ex.: "Tupã/ Arcoíris"
// vira "Tupã") e junta iguais sem diferenciar acento nem maiúscula.
// ---------------------------------------------------------------------------

export function cidadePrincipal(cidade: string | null): string {
  if (!cidade) return '';
  return cidade.split(/[/,]/)[0].replace(/\s+/g, ' ').trim();
}

export function chaveCidade(cidade: string | null): string {
  return cidadePrincipal(cidade)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}